import React, { useState, useEffect, useCallback, useRef } from "react";
import { Editor, createShapeId, AssetRecordType } from "tldraw";
import "tldraw/tldraw.css";
import "./App.css";
import { useAuth } from "./AuthContext";
import { Header, FilesPanel } from "./LeftPanel";
import FilesView from "./FilesView";
import StreamView from "./StreamView";
import TabBar from "./TabBar";
import CanvasToolbar from "./CanvasToolbar";
import SyncedEditor from "./SyncedEditor";
import {
  fetchNotes,
  fetchNote,
  fetchFolders,
  createNote,
  deleteNote,
  setLastNote as apiSetLastNote,
  setOpenNotes as apiSetOpenNotes,
  setExpandedFolders as apiSetExpandedFolders,
  setPanelWidth as apiSetPanelWidth,
  exportNotePdf,
  NoteListItem,
  Folder,
} from "./api";

interface AppProps {
  onAdminClick?: () => void;
  onProfileClick?: () => void;
}

interface CachedNote {
  name: string;
  folderId: number | null;
}

export default function App({ onAdminClick, onProfileClick }: AppProps) {
  const { user, logout } = useAuth();
  const [notes, setNotes] = useState<NoteListItem[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [folderFocused, setFolderFocused] = useState(false);
  const [streamPanelOpen, setStreamPanelOpen] = useState(false);
  const [isStyleOpen, setIsStyleOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [panelWidth, setPanelWidth] = useState(() => user?.panel_width ?? 400);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef(0);

  // Multi-tab state
  const [openTabs, setOpenTabs] = useState<number[]>([]);
  const [activeTabId, setActiveTabId] = useState<number | null>(null);
  const [noteCache, setNoteCache] = useState<Map<number, CachedNote>>(new Map());
  const editorRefs = useRef<Map<number, Editor>>(new Map());
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const loadingRef = useRef(false);
  const initialLoadDone = useRef(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<number | null>>(() => {
    if (user?.expanded_folders) {
      return new Set(user.expanded_folders);
    }
    return new Set([null]);
  });


  // Load notes list and restore open tabs on mount
  useEffect(() => {
    const init = async () => {
      await Promise.all([loadNotes(), loadFolders()]);
      if (!initialLoadDone.current && user) {
        initialLoadDone.current = true;
        if (user.expanded_folders) {
          setExpandedFolders(new Set(user.expanded_folders));
        }
        if (user.open_notes && user.open_notes.length > 0) {
          await restoreOpenTabs(user.open_notes, user.last_note_id);
        }
      }
    };
    init();
  }, [user]);

  const handleExpandedFoldersChange = useCallback((folderSet: Set<number | null>) => {
    setExpandedFolders(folderSet);
    apiSetExpandedFolders(Array.from(folderSet)).catch(console.error);
  }, []);

  const loadNotes = async () => {
    try {
      const notesList = await fetchNotes();
      setNotes(notesList);
    } catch (err) {
      console.error("Failed to load notes:", err);
    }
  };

  const loadFolders = async () => {
    try {
      const folderList = await fetchFolders();
      setFolders(folderList);
    } catch (err) {
      console.error("Failed to load folders:", err);
    }
  };

  // Restore multiple tabs on initial load
  const restoreOpenTabs = async (noteIds: number[], lastNoteId: number | null) => {
    const validIds: number[] = [];
    const newCache = new Map<number, CachedNote>();

    for (const noteId of noteIds) {
      try {
        const note = await fetchNote(noteId);
        validIds.push(noteId);
        newCache.set(noteId, {
          name: note.name,
          folderId: note.folder_id,
        });
      } catch {
        // Note no longer exists, skip it
      }
    }

    if (validIds.length > 0) {
      setNoteCache(newCache);
      setOpenTabs(validIds);
      const activeId = lastNoteId && validIds.includes(lastNoteId) ? lastNoteId : validIds[0];
      setActiveTabId(activeId);
      if (validIds.length !== noteIds.length) {
        apiSetOpenNotes(validIds).catch(console.error);
      }
    }
  };

  const persistOpenTabs = useCallback((tabs: number[]) => {
    apiSetOpenNotes(tabs).catch(console.error);
  }, []);

  // Switch to an existing tab
  const switchTab = useCallback((noteId: number) => {
    if (noteId === activeTabId) return;
    setActiveTabId(noteId);
    setFolderFocused(false);
    const cached = noteCache.get(noteId);
    if (cached) {
      setCurrentFolderId(cached.folderId);
    }

    apiSetLastNote(noteId).catch(console.error);
  }, [activeTabId, noteCache]);

  // Open a note in a new tab or switch to existing
  const openTab = useCallback(async (noteId: number) => {
    if (loadingRef.current) return;

    if (openTabs.includes(noteId)) {
      switchTab(noteId);
      return;
    }

    loadingRef.current = true;

    try {
      const note = await fetchNote(noteId);

      const cached: CachedNote = {
        name: note.name,
        folderId: note.folder_id,
      };

      setNoteCache(prev => new Map(prev).set(noteId, cached));
      const newTabs = [...openTabs, noteId];
      setOpenTabs(newTabs);
      setActiveTabId(noteId);

      persistOpenTabs(newTabs);
      apiSetLastNote(noteId).catch(console.error);
      setStatusMessage("Opened note");
      setTimeout(() => setStatusMessage(""), 1000);
    } catch (err) {
      console.error("Failed to open note:", err);
      setStatusMessage("Failed to open note");
    } finally {
      loadingRef.current = false;
    }
  }, [openTabs, persistOpenTabs, switchTab]);

  // Close a tab
  const closeTab = useCallback((noteId: number) => {
    const newTabs = openTabs.filter(id => id !== noteId);
    setOpenTabs(newTabs);

    setNoteCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(noteId);
      return newCache;
    });

    editorRefs.current.delete(noteId);

    if (noteId === activeTabId) {
      if (newTabs.length > 0) {
        const newActiveId = newTabs[newTabs.length - 1];
        setActiveTabId(newActiveId);
        apiSetLastNote(newActiveId).catch(console.error);
      } else {
        setActiveTabId(null);
        apiSetLastNote(null).catch(console.error);
      }
    }

    persistOpenTabs(newTabs);
  }, [openTabs, activeTabId, persistOpenTabs]);

  // Close multiple tabs (used when folder with notes is deleted)
  const closeNotes = useCallback((noteIds: number[]) => {
    const noteIdSet = new Set(noteIds);
    const newTabs = openTabs.filter(id => !noteIdSet.has(id));
    setOpenTabs(newTabs);

    setNoteCache(prev => {
      const newCache = new Map(prev);
      for (const noteId of noteIds) {
        newCache.delete(noteId);
        editorRefs.current.delete(noteId);
      }
      return newCache;
    });

    if (activeTabId !== null && noteIdSet.has(activeTabId)) {
      if (newTabs.length > 0) {
        const newActiveId = newTabs[newTabs.length - 1];
        setActiveTabId(newActiveId);
        apiSetLastNote(newActiveId).catch(console.error);
      } else {
        setActiveTabId(null);
        apiSetLastNote(null).catch(console.error);
      }
    }

    persistOpenTabs(newTabs);
  }, [openTabs, activeTabId, persistOpenTabs]);

  const handleNewNote = async (folderId: number | null) => {
    const name = prompt("Enter note name:");
    if (!name) return;

    try {
      const note = await createNote(name, undefined, folderId);
      await loadNotes();

      const cached: CachedNote = {
        name: note.name,
        folderId: folderId,
      };

      setNoteCache(prev => new Map(prev).set(note.id, cached));
      const newTabs = [...openTabs, note.id];
      setOpenTabs(newTabs);
      setActiveTabId(note.id);

      persistOpenTabs(newTabs);
      apiSetLastNote(note.id).catch(console.error);
      setStatusMessage("Created new note");
      setTimeout(() => setStatusMessage(""), 1000);
    } catch (err) {
      console.error("Failed to create note:", err);
      setStatusMessage("Failed to create note");
    }
  };

  const handleDeleteNoteFromExplorer = async (noteId: number) => {
    try {
      await deleteNote(noteId);

      if (openTabs.includes(noteId)) {
        const newTabs = openTabs.filter(id => id !== noteId);
        setOpenTabs(newTabs);
        setNoteCache(prev => {
          const newCache = new Map(prev);
          newCache.delete(noteId);
          return newCache;
        });
        editorRefs.current.delete(noteId);

        if (noteId === activeTabId) {
          if (newTabs.length > 0) {
            const newActiveId = newTabs[newTabs.length - 1];
            setActiveTabId(newActiveId);
            apiSetLastNote(newActiveId).catch(console.error);
          } else {
            setActiveTabId(null);
            apiSetLastNote(null).catch(console.error);
          }
        }
        persistOpenTabs(newTabs);
      }

      await loadNotes();
      setStatusMessage("Deleted");
      setTimeout(() => setStatusMessage(""), 1000);
    } catch (err) {
      console.error("Failed to delete:", err);
      setStatusMessage("Failed to delete");
    }
  };

  const handleNoteSelect = async (noteId: number) => {
    setFolderFocused(false);
    const note = notes.find(n => n.id === noteId);
    if (note) {
      setCurrentFolderId(note.folder_id);
    }
    await openTab(noteId);
  };

  const handleFolderSelect = (folderId: number | null) => {
    setCurrentFolderId(folderId);
    setFolderFocused(true);
  };

  const handleRenameNote = (noteId: number, newName: string) => {
    setNoteCache(prev => {
      const cached = prev.get(noteId);
      if (!cached) return prev;
      const newCache = new Map(prev);
      newCache.set(noteId, { ...cached, name: newName });
      return newCache;
    });
  };

  const panelWidthSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePanelWidthChange = useCallback((width: number) => {
    setPanelWidth(width);
    // Save panel width globally (debounced)
    if (panelWidthSaveTimer.current) {
      clearTimeout(panelWidthSaveTimer.current);
    }
    panelWidthSaveTimer.current = setTimeout(async () => {
      try {
        await apiSetPanelWidth(width);
      } catch (err) {
        console.error("Failed to save panel width:", err);
      }
    }, 1000);
  }, []);

  const handleScreenshot = useCallback((dataUrl: string) => {
    if (activeTabId === null) {
      setStatusMessage("No note open");
      return;
    }

    const editor = editorRefs.current.get(activeTabId);
    if (!editor) {
      setStatusMessage("Editor not ready");
      return;
    }

    const assetId = AssetRecordType.createId();
    const img = new Image();

    img.onload = () => {
      const maxSize = 800;
      let width = img.width;
      let height = img.height;

      if (width > maxSize || height > maxSize) {
        const scale = maxSize / Math.max(width, height);
        width *= scale;
        height *= scale;
      }

      editor.createAssets([
        {
          id: assetId,
          type: "image",
          typeName: "asset",
          props: {
            name: "screenshot.jpg",
            src: dataUrl,
            w: img.width,
            h: img.height,
            mimeType: "image/jpeg",
            isAnimated: false,
          },
          meta: {},
        },
      ]);

      const viewport = editor.getViewportPageBounds();
      const shapeId = createShapeId();

      editor.createShape({
        id: shapeId,
        type: "image",
        x: viewport.x + (viewport.width - width) / 2,
        y: viewport.y + (viewport.height - height) / 2,
        props: {
          assetId,
          w: width,
          h: height,
        },
      });

      editor.select(shapeId);
      editor.setCurrentTool("select");
    };

    img.src = dataUrl;
  }, [activeTabId]);

  const handleEditorMount = useCallback((tabId: number, editor: Editor) => {
    editorRefs.current.set(tabId, editor);
    if (tabId === activeTabId) {
      setActiveEditor(editor);
    }
  }, [activeTabId]);

  // Keep activeEditor in sync with the active tab so CanvasToolbar re-renders.
  useEffect(() => {
    if (activeTabId === null) {
      setActiveEditor(null);
      return;
    }
    const e = editorRefs.current.get(activeTabId);
    if (e) setActiveEditor(e);
  }, [activeTabId]);

  const handleExportPdf = useCallback(async (noteId: number) => {
    const editor = editorRefs.current.get(noteId);
    if (!editor) {
      setStatusMessage("Note not open - open the note first");
      setTimeout(() => setStatusMessage(""), 2000);
      return;
    }

    const shapeIds = [...editor.getCurrentPageShapeIds()];
    if (shapeIds.length === 0) {
      setStatusMessage("Nothing to export");
      setTimeout(() => setStatusMessage(""), 2000);
      return;
    }

    setStatusMessage("Exporting PDF...");

    try {
      const result = await editor.getSvgString(shapeIds);
      if (!result) {
        setStatusMessage("Failed to generate SVG");
        setTimeout(() => setStatusMessage(""), 2000);
        return;
      }

      const cached = noteCache.get(noteId);
      const note = notes.find(n => n.id === noteId);
      const name = cached?.name || note?.name || `note-${noteId}`;

      const blob = await exportNotePdf(noteId, result.svg, result.width, result.height, name);

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      setStatusMessage("PDF exported");
      setTimeout(() => setStatusMessage(""), 2000);
    } catch (err) {
      console.error("Failed to export PDF:", err);
      setStatusMessage("Failed to export PDF");
      setTimeout(() => setStatusMessage(""), 2000);
    }
  }, [noteCache, notes]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    dragOffsetRef.current = e.clientX - panelWidth;
    setIsDragging(true);
  }, [panelWidth]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const newWidth = Math.max(200, Math.min(e.clientX - dragOffsetRef.current, window.innerWidth - 300));
      handlePanelWidthChange(newWidth);
    },
    [isDragging, handlePanelWidthChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      dragOffsetRef.current = touch.clientX - panelWidth;
    }
    setIsDragging(true);
  }, [panelWidth]);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      if (touch) {
        const newWidth = Math.max(200, Math.min(touch.clientX - dragOffsetRef.current, window.innerWidth - 300));
        handlePanelWidthChange(newWidth);
      }
    },
    [isDragging, handlePanelWidthChange]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const tabsData = openTabs.map(id => {
    const cached = noteCache.get(id);
    const note = notes.find(n => n.id === id);
    const folderId = cached?.folderId ?? note?.folder_id;
    const folder = folderId != null ? folders.find(f => f.id === folderId) : null;
    return {
      id,
      name: cached?.name || note?.name || `Note ${id}`,
      color: folder?.color || null,
    };
  });

  return (
    <div className="app">
      <Header
        collapsed={panelCollapsed}
        onCollapsedChange={setPanelCollapsed}
        username={user?.username}
        isAdmin={user?.is_admin === 1}
        onAdminClick={onAdminClick}
        onProfileClick={onProfileClick}
        onLogout={logout}
        streamPanelOpen={streamPanelOpen}
        onStreamPanelToggle={() => setStreamPanelOpen(!streamPanelOpen)}
      />
      <div
        className={`main ${isDragging ? "dragging" : ""}`}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {(!panelCollapsed || streamPanelOpen) && (
          <FilesPanel width={panelWidth}>
            {streamPanelOpen ? (
              <div className="sidebar-stream-section full-height">
                <StreamView
                  onScreenshot={handleScreenshot}
                  onStatusMessage={setStatusMessage}
                />
              </div>
            ) : (
              <FilesView
                notes={notes}
                folders={folders}
                currentNoteId={activeTabId}
                currentFolderId={currentFolderId}
                folderFocused={folderFocused}
                openTabs={openTabs}
                expandedFolders={expandedFolders}
                onNoteSelect={handleNoteSelect}
                onFolderSelect={handleFolderSelect}
                onRefreshNotes={loadNotes}
                onRefreshFolders={loadFolders}
                onNewNote={handleNewNote}
                onDeleteNote={handleDeleteNoteFromExplorer}
                onCloseNotes={closeNotes}
                onRenameNote={handleRenameNote}
                onExpandedFoldersChange={handleExpandedFoldersChange}
                onExportPdf={handleExportPdf}
              />
            )}
          </FilesPanel>
        )}
        {(!panelCollapsed || streamPanelOpen) && (
          <div
            className="divider"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          />
        )}
        <div className="right-panel">
          <div className="top-bar">
            <TabBar
              tabs={tabsData}
              activeTabId={activeTabId}
              onTabClick={switchTab}
              onTabClose={closeTab}
            />
            <CanvasToolbar
              editor={activeEditor}
              isStyleOpen={isStyleOpen}
              onToggleStyle={() => setIsStyleOpen((v) => !v)}
            />
          </div>
          <div className="editors-container">
            {openTabs.map(tabId => (
              <div
                key={tabId}
                data-tab-id={tabId}
                className="editor-wrapper"
                style={{ display: activeTabId === tabId ? "block" : "none" }}
              >
                <SyncedEditor
                  noteId={tabId}
                  isStyleOpen={isStyleOpen}
                  onEditorMount={(editor) => handleEditorMount(tabId, editor)}
                />
              </div>
            ))}
            {openTabs.length === 0 && (
              <div className="no-note-open">
                <p>No note open</p>
                <p style={{ color: "#666", fontSize: "14px" }}>
                  Select a note from the files panel or create a new one
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {statusMessage && (
        <div className="status-toast">{statusMessage}</div>
      )}
    </div>
  );
}
